import { type AssetTypeIcon } from '@ikegps/core-api';
import { AssetTypeSwatch } from '../../components/asset.type.swatch';
import { ColorPicker } from '../../components/color.picker/color.picker';
import { addStyles } from '../../components/theme';
import { T, Translate } from '../../i18n';
import { m, type MithrilVNode } from '../../mithril';
import { CSS, PREVIEW_SIZE, Style } from './asset.type.customizer.style';

addStyles(Style);

export interface AssetTypeCustomizerAttrs {
	icon: string;
	color: string;
	/** Icons offered in the icon picker grid. */
	icons: readonly AssetTypeIcon[];
	/** Apply a new icon, keeping the current color. */
	onPickIcon: (icon: string) => void;
	/** Apply a new color, keeping the current icon. */
	onPickColor: (color: string) => void;
	palette: readonly string[];
	/** Preset rows have a fixed icon — only their color is customizable, so the icon grid is omitted. */
	iconEditable: boolean;
	/** Degrees clockwise to rotate the preview glyph (e.g. Valve's hourglass, turned on its side). */
	rotate?: number;
}

/** Presentational — pure props-in / callbacks-out, no observable state, so it ports cleanly to a React component later. */
export const AssetTypeCustomizer = {
	view(attrs: AssetTypeCustomizerAttrs): MithrilVNode {
		// Nothing to preview until an icon is chosen — showing the fallback glyph here would look like a real pick.
		const showPreview = attrs.icon !== '';
		return m('div', { className: CSS.container }, [
			showPreview ? viewPreview(attrs) : null,
			attrs.iconEditable ? viewIconSection(attrs) : null,
			viewColorSection(attrs),
		]);
	},
};

function viewPreview(attrs: AssetTypeCustomizerAttrs): MithrilVNode {
	return m('div', { className: CSS.section }, [
		m('div', { className: CSS.caption }, Translate(T.FIELD_ASSET_TYPE_PREVIEW)),
		m(
			'div',
			{ className: CSS.preview },
			AssetTypeSwatch.view({ icon: attrs.icon, color: attrs.color, size: PREVIEW_SIZE, rotate: attrs.rotate })
		),
	]);
}

function viewIconSection(attrs: AssetTypeCustomizerAttrs): MithrilVNode {
	const { icon, color, icons, onPickIcon } = attrs;
	return m('div', { className: CSS.section }, [
		m('div', { className: CSS.caption }, Translate(T.FIELD_ASSET_TYPE_ICON)),
		m(
			'div',
			{ className: CSS.iconGrid },
			icons.map((choice) => {
				const active = choice.id === icon;
				return m(
					'button',
					{
						type: 'button',
						className: active ? `${CSS.chip} ${CSS.chipActive}` : CSS.chip,
						title: choice.name,
						onclick: () => onPickIcon(choice.id),
					},
					m('div', { className: CSS.chipIcon }, AssetTypeSwatch.view({ icon: choice.id, color, size: 20 }))
				);
			})
		),
	]);
}

function viewColorSection(attrs: AssetTypeCustomizerAttrs): MithrilVNode {
	const { color, palette, onPickColor } = attrs;
	return m('div', { className: CSS.section }, [
		m('div', { className: CSS.caption }, Translate(T.COLOR)),
		ColorPicker.view({ color, palette, onPickColor }),
	]);
}
